import Mustache from "mustache"
import debounce from "lodash/debounce"
import { createEventListenerManager } from "shared/lib/utils.js"
import { RecommendedServices } from "./recommended_services"
import { RecentSearches } from "./recent_searches"
import { recommendationTemplate } from "./templates"
import { saveUserSearch, createSearchItemFromElement, SearchEntryInterface } from "./search_history"
import { SearchHelperNavigation } from "./search_helper_navigation"

const { addListener: addEventListenerWithCleanup, cleanup } = createEventListenerManager() as {
  addListener: (element: Element | Document, event: string, handler: EventListener) => void
  cleanup: (() => void) & { withPageRestore: (restoreFn: () => void) => void }
}

export const UniversalSearchHeader = async () => {
  const universalSearch = document.querySelector("#universal-search")
  if (!universalSearch) return

  // unhide the dropdown, hidden by default to avoid FOUC
  // unhide the search input, hidden by default on mobile
  universalSearch.parentElement?.removeAttribute("style")
  universalSearch.querySelector(".universal-search-helper")?.removeAttribute("style")

  const inputSearch = universalSearch.querySelector("input")
  const searchResultsContainer = universalSearch.querySelector(".search-preview > ul")

  RecentSearches()
  const allServices = await RecommendedServices()
  SearchHelperNavigation(universalSearch)

  // Lazy-load Fuse.js (reduces initial bundle ~12KB). Prefetch in requestIdleCallback
  // so first type has minimal lag; still loads on first search if user types before idle.
  let fusePromise: Promise<{ search: (term: string) => Array<{ item: unknown }> }> | null = null
  const getFuse = () => {
    if (!fusePromise) {
      fusePromise = import("fuse.js").then(({ default: Fuse }) =>
        new Fuse(allServices, { includeScore: true, threshold: 0.3, keys: ["name"] })
      )
    }
    return fusePromise
  }

  const triggerSearch = (event: Event) => {
    const keyboardEvent = event as KeyboardEvent
    const searchTerm = (keyboardEvent.target as HTMLInputElement).value.trim()

    if (!searchTerm) return

    if (keyboardEvent.key === "Enter") {
      window.App.Utils.logCustomDatadogAction("universal_search_trigger_via_enter", { term: searchTerm })

      const serviceMatch = allServices.find(
        service =>
          service.name.toLowerCase() === searchTerm.toLowerCase() ||
          service.module_name.toLowerCase() === searchTerm.toLowerCase()
      )

      if (serviceMatch) {
        // Exact match with a service name, navigate to the service page
        saveUserSearch({
          type: "service",
          term: searchTerm,
          details: {
            name: serviceMatch.name,
            brand_color: serviceMatch.brand_color,
            module_name: serviceMatch.module_name,
            image: serviceMatch.image,
          }
        })
        window.location.assign(`/${serviceMatch.module_name}`)
      } else {
        saveUserSearch({ type: "search", term: searchTerm })
        window.location.assign(`/search/query/${encodeURIComponent(searchTerm.toLowerCase())}`)
      }
    }
  }

  const handleSearchInput = (searchResultsContainer: Element) => async (event: Event) => {
    const searchTerm = (event.target as HTMLInputElement).value.trim()

    if (!searchTerm) return

    const fuse = await getFuse()
    const results = fuse.search(searchTerm).slice(0, 12)

    if (searchResultsContainer) {
      searchResultsContainer.innerHTML = ""
      results.forEach(result => {
        const rendered = Mustache.render(recommendationTemplate, result.item)
        searchResultsContainer.insertAdjacentHTML("beforeend", rendered)
      })
    }
  }

  const recommendedServiceClick = (event: Event) => {
    const serviceAnchor = (event.target as HTMLElement).closest("li > a") as HTMLAnchorElement | null

    const searchEntry = createSearchItemFromElement(serviceAnchor) as SearchEntryInterface | null
    if (!searchEntry) return
    saveUserSearch(searchEntry)
    if (inputSearch.value.trim()) {
      // Click on filtered recommended services
      window.App.Utils.logCustomDatadogAction(`universal_search_service_filter_clicked`, {
        service: serviceAnchor?.dataset?.term,
      })
    } else {
      // On explore, clicking on recent search that is a service, the result is a service search (same from recommended service).
      // On header, even on recent search, the service click will navigate to the service page.
      if (searchEntry.type === "search") {
        window.App.Utils.logCustomDatadogAction(`universal_search_recent_searches_clicked`, {
          term: serviceAnchor?.dataset?.term,
        })
      } else {
        window.App.Utils.logCustomDatadogAction(`universal_search_recommended_service_clicked`, {
          service: serviceAnchor?.dataset?.term,
        })
      }
    }
  }

  const debouncedHandleInput = debounce(handleSearchInput(searchResultsContainer), 100)
  addEventListenerWithCleanup(inputSearch, "input", debouncedHandleInput)
  addEventListenerWithCleanup(inputSearch, "keydown", triggerSearch)

  // Prefetch Fuse when main thread is idle to reduce first-type lag (review: cjanik).
  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(() => getFuse(), { timeout: 2000 })
  } else {
    setTimeout(() => getFuse(), 1)
  }

  const recentSearchesWrapper = universalSearch.querySelector(".recent-searches")
  if (recentSearchesWrapper) {
    addEventListenerWithCleanup(recentSearchesWrapper, "click", recommendedServiceClick)
  }

  const recommendedServicesWrapper = universalSearch.querySelector(".recommended-services")
  if (recommendedServicesWrapper) {
    addEventListenerWithCleanup(recommendedServicesWrapper, "click", recommendedServiceClick)
  }

  const filteredServicesWrapper = universalSearch.querySelector(".search-preview")
  if (filteredServicesWrapper) {
    addEventListenerWithCleanup(filteredServicesWrapper, "click", recommendedServiceClick)
  }

  const searchNavigation = universalSearch.querySelector("a.search-navigation")
  if (searchNavigation) {
    addEventListenerWithCleanup(searchNavigation, "click", () => {
      window.App.Utils.logCustomDatadogAction("universal_search_trigger_via_navigation_link", {
        term: inputSearch?.value.trim(),
      })
    })
  }

  addEventListenerWithCleanup(inputSearch, "focus", () => {
    window.App.Utils.logCustomDatadogAction("universal_search_click_search_bar")
  })

  const mobileIconSearch = document.querySelector("span.mobile-icon-search")
  if (mobileIconSearch) {
    addEventListenerWithCleanup(mobileIconSearch, "click", () => {
      document.body.classList.add("mobile-universal-search-enabled")
      inputSearch?.focus()
      window.App.Utils.logCustomDatadogAction("mobile_universal_search_view")
    })
  }

  const mobileIconClose = document.querySelector(".mobile-close-search-icon")
  if (mobileIconClose) {
    addEventListenerWithCleanup(mobileIconClose, "click", () => {
      document.body.classList.remove("mobile-universal-search-enabled")
      inputSearch?.blur()
    })
  }

  addEventListenerWithCleanup(window, "scroll", () => {
    if (!document.body.classList.contains("mobile-universal-search-enabled")) return
    inputSearch?.blur()
  })

  cleanup.withPageRestore(() => {
    if (inputSearch) {
      inputSearch.value = ""
      inputSearch.dispatchEvent(new Event("input", { bubbles: true }))
    }
    if (searchResultsContainer) searchResultsContainer.innerHTML = ""

    UniversalSearchHeader()
  })

  window.addEventListener("beforeunload", cleanup)
}
